import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "ui/Scripts/source/framework/base";
import { Dialog } from "../framework/dialog";
import * as taskService from "../../models/services/taskService";
import { IEndlessList, EndlessList } from "ui/Scripts/source/models/common/endlessList";
import { IUnitItem, UnitItem } from "../../models/unit/unitItem";
import { ITaskStateItem, TaskStateItem } from "../../models/task/taskStateItem";
import { SelectTaskStateDialog, SelectTaskStateDialogLine } from "./selectTaskStateDialog";
import { SelectUnitDialog, SelectUnitDialogLine } from "../event/selectUnitDialog";
import { DialogButtonType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "source/framework/appUtils";

//TaskListSearchDialog
//***************************************************************
export interface ITaskListSearchDialogProp {
    stateId: string;
    unitId: string;
    units: IUnitItem[];
    taskStates: ITaskStateItem[];
    companyLogo: string;
    classes?: string;
    onOk: (stateId: string, unitId: string) => void;
    onCancel: () => void;
}

export interface ITaskListSearchDialogState {
    units: IUnitItem[];
    unitsHaveComment: boolean;
    taskStates: ITaskStateItem[];
    stateId: string;
    unitId: string;
    showEventStateDialog: boolean;
    showEventUnitDialog: boolean;
}

export class TaskListSearchDialog extends React.Component<ITaskListSearchDialogProp, ITaskListSearchDialogState> {
    private getTaskStates(taskStates: ITaskStateItem[]): ITaskStateItem[] {
        const result: ITaskStateItem[] = [];
        const allUnit = new TaskStateItem();
        allUnit.id = Base.emptyGuid;
        allUnit.code = "'" + Translations.All + "'";
        result.push(allUnit);
        for (let i = 0; i < taskStates.length; i++) {
            result.push(new TaskStateItem(taskStates[i]));
        }
        return result;
    }

    private getTaskStateById(taskStates: ITaskStateItem[], stateId: string): ITaskStateItem {
        let unit = taskStates.find(i => i.id === stateId);
        if (Base.isNullOrUndefined(unit)) {
            unit = taskStates[0];
        }
        return unit;
    }

    private getUnits(props: ITaskListSearchDialogProp): IUnitItem[] {
        const result: IUnitItem[] = [];
        const allUnit = new UnitItem();
        allUnit.id = Base.emptyGuid;
        allUnit.name = "'" + Translations.All + "'";
        result.push(allUnit);
        for (let i = 0; i < props.units.length; i++) {
            result.push(new UnitItem(props.units[i]));
        }
        return result;
    }

    private getUnitById(units: IUnitItem[], unitId: string): IUnitItem {
        let unit = units.find(i => i.id === unitId);
        if (Base.isNullOrUndefined(unit)) {
            unit = units[0];
        }
        return unit;
    }

    private filterTaskStatesByUnitId = async (unitId: string) => {
        let stateId = this.state.stateId;
        let taskStates: ITaskStateItem[] = this.props.taskStates;
        if (unitId !== Base.emptyGuid) {
            const result = await AppUtils.callService(() => taskService.getUsedTaskStates(unitId), false);
            if (!result) return;
            taskStates = result.taskStates;
        }
        if (stateId !== Base.emptyGuid && !taskStates.find(i => i.id === stateId)) {
            stateId = Base.emptyGuid;
        }
        this.setState({
            stateId,
            taskStates: this.getTaskStates(taskStates)
        });
    };

    constructor(props: ITaskListSearchDialogProp) {
        super(props);
        const units = this.getUnits(props);
        this.state = {
            units,
            taskStates: this.getTaskStates(props.taskStates),
            unitsHaveComment: units.findIndex(i => !Base.isNullOrEmpty(i.comment)) > -1,
            stateId: props.stateId,
            unitId: props.unitId,
            showEventStateDialog: false,
            showEventUnitDialog: false
        };
        if (props.unitId !== Base.emptyGuid) {
            this.filterTaskStatesByUnitId(props.unitId);
        }
    }

    handleUnitClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ showEventUnitDialog: true });
    };

    handleLoadUnits = (page: number): Promise<IEndlessList<IUnitItem>> => {
        const obj = this;
        return new Promise<IEndlessList<IUnitItem>>((resolve, reject) => {
            const result = new EndlessList<IUnitItem>(null, UnitItem);
            result.items = obj.state.units;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    handleSelectUnitDialogOk = async (id: string, code: string) => {
        this.setState({
            unitId: id,
            showEventUnitDialog: false
        });
        this.filterTaskStatesByUnitId(id);
    };

    handleSelectUnitDialogCancel = () => {
        this.setState({
            showEventUnitDialog: false
        });
    };

    handleStateClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ showEventStateDialog: true });
    };

    handleLoadTaskStates = (page: number): Promise<IEndlessList<ITaskStateItem>> => {
        const obj = this;
        return new Promise<IEndlessList<ITaskStateItem>>((resolve, reject) => {
            const result = new EndlessList<ITaskStateItem>(null, TaskStateItem);
            result.items = obj.state.taskStates;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    handleSelectTaskStateDialogOk = (id: string, code: string) => {
        this.setState({
            stateId: id,
            showEventStateDialog: false
        });
    };

    handleSelectTaskStateDialogCancel = () => {
        this.setState({
            showEventStateDialog: false
        });
    };

    handleOkClick = () => {
        this.props.onOk(!Base.isNullOrEmpty(this.state.stateId) && this.state.stateId !== Base.emptyGuid ? this.state.stateId : null,
            !Base.isNullOrEmpty(this.state.unitId) && this.state.unitId !== Base.emptyGuid ? this.state.unitId : null);
    };

    handleCancelClick = () => {
        this.props.onCancel();
    };

    handleClearClick = () => {
        this.setState({ stateId: "", unitId: "" });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const units = state.units;
        const taskStates = state.taskStates;
        const taskUnit = this.getUnitById(units, state.unitId);
        const taskState = this.getTaskStateById(taskStates, state.stateId);
        const dialogClasses = "taskListSearchDialog" + (!Base.isNullOrEmpty(props.classes) ? " " + props.classes : "");
        return (
            <Dialog
                classes={dialogClasses}
                title={Translations.SearchTasks}
                body={
                    <div>
                        {this.props.units.length > 1 &&
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group roWhite">
                                        <label className="control-label">{Translations.Unit}</label>
                                        <SelectUnitDialogLine
                                            classes={"form-control unitLine"}
                                            item={taskUnit}
                                            companyLogo={props.companyLogo}
                                            unitsHaveComment={state.unitsHaveComment}
                                            selected={false}
                                            onClick={(id: string) => { this.setState({ showEventUnitDialog: true }); }}
                                        />
                                    </div>
                                </div>
                            </div>
                        }
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group roWhite">
                                    <label className="control-label">{Translations.State}</label>
                                    <SelectTaskStateDialogLine
                                        classes={"form-control unitLine"}
                                        item={taskState}
                                        selected={false}
                                        onClick={(id: string) => { this.setState({ showEventStateDialog: true }); }}
                                    />
                                </div>
                            </div>
                        </div>
                        {state.showEventStateDialog &&
                            <SelectTaskStateDialog
                                title={Translations.SelectState}
                                selectedId={state.stateId}
                                loadTaskStates={this.handleLoadTaskStates}
                                onOk={this.handleSelectTaskStateDialogOk}
                                onCancel={this.handleSelectTaskStateDialogCancel}
                            />
                        }
                        {state.showEventUnitDialog &&
                            <SelectUnitDialog
                                title={Translations.SelectUnit}
                                selectedId={state.unitId}
                                companyLogo={props.companyLogo}
                                loadUnits={this.handleLoadUnits}
                                onOk={this.handleSelectUnitDialogOk}
                                onCancel={this.handleSelectUnitDialogCancel}
                            />
                        }
                    </div>
                }
                buttons={[
                    { title: Translations.Clear, buttonType: DialogButtonType.Default, classes: "mr-auto", onClick: this.handleClearClick },
                    { title: Translations.OK, buttonType: DialogButtonType.Primary, onClick: this.handleOkClick },
                    { title: Translations.Cancel, buttonType: DialogButtonType.Default, onClick: this.handleCancelClick }
                ]}
                onClose={this.handleCancelClick}/>
        );
    }
}